"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider } from "next-themes";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { Suspense, useState, useEffect } from "react";
import { Toaster as SonnerToaster } from "sonner";
import { ChatSupportLauncher } from "@/components/chat-support-launcher";
import { ReferralHandler } from "@/components/referral-handler";
import { SignupMethodTracker } from "@/components/signup-method-tracker";
import { classifyChannel } from "@/lib/attribution";
import { Toaster } from "@/lib/components/toaster";
import { toast } from "@/lib/components/use-toast";
import { AppConfigProvider } from "@/lib/config";
import { SystemBannerProvider } from "@/lib/system-banner-context";
import { TimeZoneProvider } from "@llmgateway/shared";
import { ProviderMarksProvider } from "@llmgateway/shared/components";
import type { AppConfig } from "@/lib/config-server";
import type { SystemBanner, TimeZonePreference } from "@llmgateway/shared";
import type { ReactNode } from "react";
interface ProvidersProps {
  children: ReactNode;
  config: AppConfig;
  /** Read from the timezone cookie by the root layout, so the first render
   *  already uses the user's chosen zone. */
  timeZone: TimeZonePreference;
  /** Admin-toggled announcement banner, fetched by the root layout. */
  systemBanner: SystemBanner | null;
}
function extractErrorMessage(error: unknown): string {
  if (typeof error === "object" && error !== null) {
    const err = error as Record<string, unknown>;
    if (err.error && typeof err.error === "object") {
      const nestedError = err.error as Record<string, unknown>;
      if (typeof nestedError.message === "string") {
        return nestedError.message;
      }
    }
    if (typeof err.message === "string") {
      return err.message;
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return "An unknown error occurred.";
}
export function Providers(t0) {
  const $ = _c(31);
  const {
    children,
    config,
    timeZone,
    systemBanner
  } = t0;
  const [queryClient] = useState(_temp2);
  const [posthogReady, setPosthogReady] = useState(false);
  let t1;
  let t2;
  if ($[0] !== config.posthogHost || $[1] !== config.posthogKey) {
    t1 = () => {
      if (!config.posthogKey) {
        return;
      }
      const key = config.posthogKey;
      const host = config.posthogHost;
      const init = () => {
        posthog.init(key, {
          api_host: "/ingest",
          ui_host: host,
          capture_pageview: "history_change",
          autocapture: true
        });
        const channel = classifyChannel(document.referrer, window.location.search, window.location.hostname);
        posthog.register({
          acquisition_channel: channel
        });
        posthog.setPersonProperties(undefined, {
          initial_acquisition_channel: channel
        });
        setPosthogReady(true);
      };
      if (typeof requestIdleCallback !== "undefined") {
        const id = requestIdleCallback(init, {
          timeout: 800
        });
        return () => cancelIdleCallback(id);
      }
      const timer = setTimeout(init, 300);
      return () => clearTimeout(timer);
    };
    t2 = [config.posthogKey, config.posthogHost];
    $[0] = config.posthogHost;
    $[1] = config.posthogKey;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  if ($[4] !== children || $[5] !== config.apiUrl) {
    t3 = <ProviderMarksProvider apiUrl={config.apiUrl}>{children}</ProviderMarksProvider>;
    $[4] = children;
    $[5] = config.apiUrl;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== posthogReady) {
    t4 = posthogReady && <Suspense><SignupMethodTracker /></Suspense>;
    $[7] = posthogReady;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <PostHogProvider client={posthog}>{t3}{t4}</PostHogProvider>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = process.env.NODE_ENV === "development" && <ReactQueryDevtools buttonPosition="bottom-left" />;
    t7 = <ChatSupportLauncher />;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  let t8;
  if ($[14] !== queryClient || $[15] !== t5) {
    t8 = <QueryClientProvider client={queryClient}>{t5}{t6}{t7}</QueryClientProvider>;
    $[14] = queryClient;
    $[15] = t5;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t10;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <Toaster />;
    t10 = <SonnerToaster richColors={true} position="bottom-right" />;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Suspense><ReferralHandler /></Suspense>;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t8) {
    t12 = <ThemeProvider attribute="class" defaultTheme="system" enableSystem={true} storageKey="theme">{t8}{t9}{t10}{t11}</ThemeProvider>;
    $[20] = t8;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== systemBanner || $[23] !== t12) {
    t13 = <SystemBannerProvider banner={systemBanner}>{t12}</SystemBannerProvider>;
    $[22] = systemBanner;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== config || $[26] !== t13) {
    t14 = <AppConfigProvider config={config}>{t13}</AppConfigProvider>;
    $[25] = config;
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14 || $[29] !== timeZone) {
    t15 = <TimeZoneProvider initial={timeZone}>{t14}</TimeZoneProvider>;
    $[28] = t14;
    $[29] = timeZone;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
}
function _temp2() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        staleTime: 300000,
        retry: false
      },
      mutations: {
        onError: _temp
      }
    }
  });
}
function _temp(error) {
  const errorMessage = extractErrorMessage(error);
  toast({
    title: errorMessage,
    variant: "destructive"
  });
}